import React, { useState, useEffect } from 'react';
import { View, Text, ScrollView, TouchableOpacity, TextInput } from 'react-native';
import { getFirestore, collection, query, where, getDocs, doc, updateDoc, addDoc } from 'firebase/firestore';
import { getAuth } from 'firebase/auth';
import { adminStyles } from '../../styles/adminStyles';
import VenueCard from './VenueCard';

export function NewVenues() {
  const [newVenues, setNewVenues] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterStatus, setFilterStatus] = useState('pending'); // pending, approved, rejected, all
  const [loading, setLoading] = useState(true);
  const [editingVenue, setEditingVenue] = useState(null);
  const [editFormData, setEditFormData] = useState({});

  useEffect(() => {
    loadNewVenues();
  }, [filterStatus]);

  const loadNewVenues = async () => {
    try {
      setLoading(true);
      const db = getFirestore();
      const venuesRef = collection(db, 'new_venues');
      
      let q;
      if (filterStatus === 'all') {
        q = query(venuesRef);
      } else {
        q = query(venuesRef, where('status', '==', filterStatus));
      }

      const querySnapshot = await getDocs(q);
      const venues = [];
      
      querySnapshot.forEach((doc) => {
        venues.push({
          id: doc.id,
          ...doc.data()
        });
      });
      
      setNewVenues(venues);
    } catch (error) {
      console.error('Error loading new venues:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = async (venue) => {
    try {
      const db = getFirestore();
      const auth = getAuth();

      // Create new venue document
      const venueData = {
        name: venue.name,
        address: venue.address || '',
        city: venue.city || '',
        state: venue.state || '',
        capacity: venue.capacity || '',
        website: venue.website || '',
        createdAt: new Date(),
        updatedAt: new Date(),
        createdBy: venue.submittedBy?.uid || auth.currentUser?.uid,
        status: 'active',
        // Technical specifications
        stage: venue.stage || '',
        power: venue.power || '',
        lightingControl: venue.lightingControl || '',
        lightingFixtures: venue.lightingFixtures || '',
        video: venue.video || '',
        foh: venue.foh || '',
        pa: venue.pa || '',
        monitors: venue.monitors || '',
        // Additional fields
        affiliate: venue.affiliate || '',
        backline: venue.backline || '',
        backstage: venue.backstage || '',
        merch: venue.merch || '',
        parking: venue.parking || '',
        shorePower: venue.shorePower || '',
        specSheetLink: venue.specSheetLink || '',
        mapEmbedLink: venue.mapEmbedLink || '',
        notes: venue.notes || ''
      };

      // Add venue to main venues collection
      const venueRef = await addDoc(collection(db, 'venues'), venueData);

      // Update submission status
      const submissionRef = doc(db, 'new_venues', venue.id);
      await updateDoc(submissionRef, {
        status: 'approved',
        venueId: venueRef.id,
        approvedAt: new Date(),
        approvedBy: auth.currentUser?.uid
      });

      // Refresh venues list
      loadNewVenues();
      alert('Venue approved successfully!');
    } catch (error) {
      console.error('Error approving venue:', error);
      alert('Error approving venue: ' + error.message);
    }
  };

  const handleReject = async (venue) => {
    try {
      const db = getFirestore();
      const auth = getAuth();

      // Update submission status
      const submissionRef = doc(db, 'new_venues', venue.id);
      await updateDoc(submissionRef, {
        status: 'rejected',
        rejectedAt: new Date(),
        rejectedBy: auth.currentUser?.uid
      });

      // Refresh venues list
      loadNewVenues();
      alert('Venue rejected successfully!');
    } catch (error) {
      console.error('Error rejecting venue:', error);
      alert('Error rejecting venue: ' + error.message);
    }
  };

  const handleRevertToPending = async (venue) => {
    try {
      const db = getFirestore();

      // Update submission status
      const submissionRef = doc(db, 'new_venues', venue.id);
      await updateDoc(submissionRef, {
        status: 'pending',
        approvedAt: null,
        rejectedAt: null,
        approvedBy: null,
        rejectedBy: null
      });

      // Refresh venues list
      loadNewVenues();
      alert('Venue reverted to pending successfully!');
    } catch (error) {
      console.error('Error reverting venue:', error);
      alert('Error reverting venue: ' + error.message);
    }
  };

  const handleEditField = (field, value) => {
    setEditFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSaveVenue = async () => {
    try {
      if (!editingVenue) return;
      
      const db = getFirestore();
      const venueRef = doc(db, 'new_venues', editingVenue.id);
      
      // Update the document with edited fields
      await updateDoc(venueRef, {
        ...editFormData,
        updatedAt: new Date()
      });
      
      // Refresh the venue list
      loadNewVenues();
      
      // Reset editing state
      setEditingVenue(null);
      setEditFormData({});
      
      alert('Venue updated successfully!');
    } catch (error) {
      console.error('Error updating venue:', error);
      alert('Error updating venue: ' + error.message);
    }
  };

  const handleCancelEdit = () => {
    setEditingVenue(null);
    setEditFormData({});
  };

  const startEditing = (venue) => {
    setEditingVenue(venue);
    setEditFormData({
      name: venue.name || '',
      address: venue.address || '',
      city: venue.city || '',
      state: venue.state || '',
      capacity: venue.capacity || '',
      website: venue.website || '',
      stage: venue.stage || '',
      power: venue.power || '',
      lightingControl: venue.lightingControl || '',
      lightingFixtures: venue.lightingFixtures || '',
      video: venue.video || '',
      foh: venue.foh || '',
      pa: venue.pa || '',
      monitors: venue.monitors || '',
      affiliate: venue.affiliate || '',
      backline: venue.backline || '',
      backstage: venue.backstage || '',
      merch: venue.merch || '',
      parking: venue.parking || '',
      shorePower: venue.shorePower || '',
      specSheetLink: venue.specSheetLink || '',
      mapEmbedLink: venue.mapEmbedLink || '',
      notes: venue.notes || '',
      manualsLinks: venue.manualsLinks || [],
      specSheets: venue.specSheets || [],
    });
  };

  const getFilteredVenues = () => {
    // First filter venues based on search criteria
    const filteredVenues = newVenues.filter(venue => {
      const searchLower = searchQuery.toLowerCase();
      return (
        venue.name?.toLowerCase().includes(searchLower) ||
        venue.city?.toLowerCase().includes(searchLower) ||
        venue.state?.toLowerCase().includes(searchLower)
      );
    });
    
    // If there's a search query, prioritize exact matches
    if (searchQuery) {
      const searchLower = searchQuery.toLowerCase();
      
      return filteredVenues.sort((a, b) => {
        // Check for exact matches (case insensitive)
        const aExactName = a.name?.toLowerCase() === searchLower;
        const bExactName = b.name?.toLowerCase() === searchLower;
        
        const aExactCity = a.city?.toLowerCase() === searchLower;
        const bExactCity = b.city?.toLowerCase() === searchLower;
        
        const aExactState = a.state?.toLowerCase() === searchLower;
        const bExactState = b.state?.toLowerCase() === searchLower;
        
        // Prioritize exact matches in name first
        if (aExactName && !bExactName) return -1;
        if (!aExactName && bExactName) return 1;
        
        // Then exact matches in city
        if (aExactCity && !bExactCity) return -1;
        if (!aExactCity && bExactCity) return 1;
        
        // Then exact matches in state
        if (aExactState && !bExactState) return -1;
        if (!aExactState && bExactState) return 1;
        
        // For partial matches, prioritize those that start with the search term
        const aStartsWithName = a.name?.toLowerCase().startsWith(searchLower);
        const bStartsWithName = b.name?.toLowerCase().startsWith(searchLower);
        
        if (aStartsWithName && !bStartsWithName) return -1;
        if (!aStartsWithName && bStartsWithName) return 1;
        
        // Fall back to alphabetical sorting by name
        return (a.name || '').localeCompare(b.name || '');
      });
    }
    
    // If no search query, return venues in their original order
    return filteredVenues;
  };

  const handleExportCSV = async () => {
    try {
      // Helper function to escape CSV field
      const escapeField = (field) => {
        if (field === null || field === undefined) return '';
        const stringField = String(field);
        
        // Trim BOM characters but preserve internal spaces and line breaks
        const cleanField = stringField.replace(/^\uFEFF/, '');
        
        // If field contains quotes, commas, or newlines, wrap in quotes and escape existing quotes
        if (cleanField.includes('"') || cleanField.includes(',') || cleanField.includes('\n') || cleanField.includes('\r')) {
          // Double the quotes but preserve line breaks
          return `"${cleanField.replace(/"/g, '""')}"`;
        }
        return cleanField;
      };

      // Fetch all approved venues from Firestore
      const db = getFirestore();
      const venuesRef = collection(db, 'new_venues');
      const q = query(venuesRef, where('status', '==', 'approved'));
      const querySnapshot = await getDocs(q);
      
      const approvedVenues = [];
      querySnapshot.forEach((doc) => {
        approvedVenues.push({
          id: doc.id,
          ...doc.data()
        });
      });

      if (approvedVenues.length === 0) {
        alert('No approved venues to export.');
        return;
      }

      // Define headers
      const headers = [
        'STATE/COUNTRY',
        'VENUE',
        'CITY',
        'WEBSITE',
        'CAPACITY',
        'AFFILIATE',
        'STAGE',
        'SPEC SHEET LINK',
        'POWER',
        'LIGHTING CONTROL',
        'LIGHTING FIXTURES',
        'VIDEO',
        'FOH',
        'PA',
        'MONITORS',
        'BACKSTAGE',
        'BACKLINE',
        'MERCH',
        'PARKING',
        'SHORE POWER',
        'NOTES',
        'MAP EMBED LINK',
        'UPDATED AT',
        'STATUS',
        'SUBMITTED AT',
        'SUBMITTED BY'
      ];

      // Create CSV content with properly escaped fields
      const csvRows = [
        headers.join(','),
        ...approvedVenues.map(venue => [
          escapeField(venue.state),
          escapeField(venue.name),
          escapeField(venue.city),
          escapeField(venue.website),
          escapeField(venue.capacity),
          escapeField(venue.affiliate),
          escapeField(venue.stage),
          escapeField(venue.specSheetLink),
          escapeField(venue.power),
          escapeField(venue.lightingControl),
          escapeField(venue.lightingFixtures),
          escapeField(venue.video),
          escapeField(venue.foh),
          escapeField(venue.pa),
          escapeField(venue.monitors),
          escapeField(venue.backstage),
          escapeField(venue.backline),
          escapeField(venue.merch),
          escapeField(venue.parking),
          escapeField(venue.shorePower),
          escapeField(venue.notes),
          escapeField(venue.mapEmbedLink),
          escapeField(venue.updatedAt ? new Date(venue.updatedAt.seconds * 1000).toISOString() : ''),
          escapeField(venue.status),
          escapeField(venue.createdAt ? new Date(venue.createdAt.seconds * 1000).toISOString() : ''),
          escapeField(venue.submittedBy?.email || '')
        ].join(','))
      ];

      // Create and download the file
      const blob = new Blob([csvRows.join('\n')], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);
      link.setAttribute('href', url);
      link.setAttribute('download', `approved_new_venues_${new Date().toISOString().split('T')[0]}.csv`);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (error) {
      console.error('Error exporting venues:', error);
      alert('Error exporting venues: ' + error.message);
    }
  };

  return (
    <View style={adminStyles.container}>
      <View style={{
        width: '100%',
        maxWidth: 1400,
        marginBottom: 20,
        padding: 20,
        gap: 15
      }}>
        <View style={{
          flexDirection: 'row',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: 10
        }}>
          <Text style={{
            fontSize: 24,
            fontWeight: 'bold',
            color: '#fff',
            fontFamily: 'VastagoGrotesk'
          }}>New Venue Submissions</Text>
          
          <TouchableOpacity
            style={{
              backgroundColor: 'rgb(61, 127, 230)',
              paddingVertical: 12,
              paddingHorizontal: 20,
              borderRadius: 8,
              alignItems: 'center',
              justifyContent: 'center',
            }}
            onPress={handleExportCSV}
          >
            <Text style={{
              color: 'white',
              fontFamily: 'VastagoGrotesk',
              fontSize: 14,
            }}>
              Export to CSV
            </Text>
          </TouchableOpacity>
        </View>
        
        <View style={{
          flexDirection: 'row',
          flexWrap: 'wrap',
          gap: 10,
          marginBottom: 15
        }}>
          {['all', 'pending', 'approved', 'rejected'].map((status) => (
            <TouchableOpacity
              key={status}
              style={{
                backgroundColor: filterStatus === status ? '#0bb5df' : 'rgba(255, 255, 255, 0.1)',
                paddingVertical: 10,
                paddingHorizontal: 20,
                borderRadius: 8,
                minWidth: 120,
                alignItems: 'center',
                justifyContent: 'center'
              }}
              onPress={() => setFilterStatus(status)}
            >
              <Text style={{
                color: 'white',
                fontFamily: 'VastagoGrotesk',
                fontSize: 14,
                fontWeight: '500'
              }}>{status.toUpperCase()}</Text>
            </TouchableOpacity>
          ))}
        </View>
        
        <View style={{
          backgroundColor: 'rgba(255, 255, 255, 0.1)',
          borderRadius: 8,
          padding: 15,
        }}>
          <TextInput
            style={{
              color: '#ffffff',
              fontFamily: 'VastagoGrotesk',
              fontSize: 16,
              width: '100%',
            }}
            placeholder="Search venues by name, city, or state..."
            placeholderTextColor="rgba(255, 255, 255, 0.5)"
            value={searchQuery}
            onChangeText={setSearchQuery}
          />
        </View>
        
        <Text style={{
          color: 'rgba(255, 255, 255, 0.7)',
          fontFamily: 'VastagoGrotesk',
          fontSize: 14,
          marginTop: 5
        }}>
          {getFilteredVenues().length} venues found
        </Text>
      </View>

      <ScrollView style={adminStyles.content}>
        {loading ? (
          <Text style={adminStyles.loadingText}>Loading venues...</Text>
        ) : getFilteredVenues().length === 0 ? (
          <Text style={adminStyles.emptyText}>No venues found</Text>
        ) : (
          getFilteredVenues().map((venue) => (
            <VenueCard
              key={venue.id}
              venue={venue}
              editingVenue={editingVenue}
              editFormData={editFormData}
              onStartEditing={startEditing}
              onApprove={handleApprove}
              onReject={handleReject}
              onRevertToPending={handleRevertToPending}
              onCancelEdit={handleCancelEdit}
              onSaveVenue={handleSaveVenue}
              onEditField={handleEditField}
            />
          ))
        )}
      </ScrollView>
    </View>
  );
} 